/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React, { useCallback } from 'react';
import type { MosaicNode } from 'react-mosaic-component';
import { useSelector, useDispatch } from 'react-redux';
import { uiActions, uiSelectors } from '@opengeoweb/store';
import {
  closeDialogsWhenChangingPreset,
  createBalancedMosaicNode,
} from './helpers';
import WorkspaceView from './WorkspaceView';
import type { AppStore } from '../../store/types';
import { workspaceActions } from '../../store/workspace/reducer';
import {
  isWorkspaceLoading as workspaceSelectors_isWorkspaceLoading,
  getWorkspaceState,
  getMosaicNode,
  getComponentTypesFromActiveViews,
} from '../../store/workspace/selectors';
import type { WorkspaceLookupFunctionType } from '../../store/workspace/types';

export interface WorkspaceViewConnectProps {
  componentsLookUp: WorkspaceLookupFunctionType;
}

const WorkspaceViewConnect: React.FC<WorkspaceViewConnectProps> = ({
  componentsLookUp,
}: WorkspaceViewConnectProps) => {
  const dispatch = useDispatch();

  const isWorkspaceLoading = useSelector((store: AppStore) =>
    workspaceSelectors_isWorkspaceLoading(store),
  );

  const screenConfig = useSelector((store: AppStore) =>
    getWorkspaceState(store),
  );

  const updateViews = useCallback(
    (currentNode: MosaicNode<string>): void => {
      dispatch(
        workspaceActions.updateWorkspaceViews({
          mosaicNode: currentNode,
        }),
      );
    },
    [dispatch],
  );

  const setActiveWindowId = useCallback(
    (activeWindowId: string): void => {
      dispatch(uiActions.setActiveWindowId({ activeWindowId }));
    },
    [dispatch],
  );

  const activeWindowId = useSelector(uiSelectors.getActiveWindowId);

  const mosaicNode = useSelector((store: AppStore) => getMosaicNode(store));

  const autoArrange = useCallback((): void => {
    dispatch(
      workspaceActions.updateWorkspaceViews({
        mosaicNode: createBalancedMosaicNode(mosaicNode),
      }),
    );
  }, [dispatch, mosaicNode]);

  const handleOrientationChange = React.useCallback(() => {
    autoArrange();
  }, [autoArrange]);

  const componentTypes = useSelector((store: AppStore) =>
    getComponentTypesFromActiveViews(store),
  );

  React.useEffect(() => {
    closeDialogsWhenChangingPreset(componentTypes, dispatch);
  }, [componentTypes, dispatch]);

  React.useEffect(() => {
    window.addEventListener('orientationchange', handleOrientationChange);
    return (): void =>
      window.removeEventListener('orientationchange', handleOrientationChange);
  }, [handleOrientationChange]);

  React.useEffect(() => {
    if (!mosaicNode) {
      dispatch(workspaceActions.teardownComplete());
    }
  }, [mosaicNode, dispatch]);

  return (
    <WorkspaceView
      activeWindowId={activeWindowId}
      componentsLookUp={componentsLookUp}
      isWorkspaceLoading={isWorkspaceLoading}
      screenConfig={screenConfig}
      setActiveWindowId={setActiveWindowId}
      updateViews={updateViews}
    />
  );
};

export default WorkspaceViewConnect;
